import React, { Fragment, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useSelector } from "react-redux";
import SelectBox from "components/FormElements/SelectBox";
import TextInput from "components/FormElements/TextInput";
import { AutomationTaskObjectSchema } from "schemas/AutomationTask";
import CallWebhookAction from "./Actions/CallWebhookAction";
import PublishAction from "./Actions/PublishAction";
import SendEmailAction from "./Actions/SendEmailAction";
import SetTagsAction from "./Actions/SetTagsAction";
import { apiPut, apiPost } from "helpers/Request";
import { useAuth } from "hooks/Auth";
import { toast } from "react-hot-toast";
import { transform as taskTransformer } from "schemas/AutomationTask";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    TrashIcon,
    PlusIcon,
} from "@heroicons/react/outline";
import { packClasses } from "Utils";
import PrimaryButton from "components/Buttons/PrimaryButton";

const contentTypes = [
    "any",
    "article",
    "media",
    "assignment",
    "image",
    "video",
    "gallery",
    "audio",
    "pdf",
];
const transitionTypes = [
    "transition",
    "publish",
    "create",
    "update",
    "meta-update",
];
const actionTypes = ["Send Email", "Publish", "Call Webhook", "Set Tags"];

const ensureTaskShape = (task) => {
    return Object.assign({}, AutomationTaskObjectSchema, task);
};

export default function AutomationTask(props) {
    const contentFlow = useSelector((state) => state.contentFlow);
    const [formValues, setFormValues] = useState(AutomationTaskObjectSchema);
    const [transitionType, setTransitionType] = useState("transition");
    const [actions, setActions] = useState([]);
    const [open, setOpen] = useState(props.open || false);
    const auth = useAuth();

    const onSubmit = (values) => {
        const data = {
            from_workflow: values.fromWorkflow,
            to_workflow: values.toWorkflow,
            content_type: values.contentType,
            actions: values.actions,
            transition_type: values.transition,
            meta_field: values.metaField ? values.metaField : null,
            meta_target: values.metaTarget ? values.metaTarget : null,
            uuid: values.uuid || undefined,
        };
        const id = toast.loading("Saving task...");
        if (values.uuid) {
            apiPut(`/v4/automation-task/${values.uuid}`, auth.publication, data)
                .then((resp) => {
                    setFormValues(ensureTaskShape(taskTransformer(resp)));
                })
                .finally(() => {
                    toast.success("Saved!", { id });
                });
        } else {
            apiPost("/v4/automation-task", auth.publication, data)
                .then((resp) => {
                    console.log(taskTransformer(resp));
                    setFormValues(ensureTaskShape(taskTransformer(resp)));
                })
                .finally(() => {
                    toast.success("Saved!", { id });
                });
        }
    };

    const addAction = () => {
        setActions([
            ...actions,
            {
                type: "Select",
                id: 99,
                properties: {},
            },
        ]);
    };

    useEffect(() => {
        setFormValues(ensureTaskShape(props.task));
        setTransitionType(props.task.transition);
        setActions(props.task.actions || []);
    }, [props.task]);

    return (
        <div>
            <Formik
                initialValues={formValues}
                onSubmit={onSubmit}
                enableReinitialize={true}
            >
                <Form className="space-y-4">
                    <div
                        className={packClasses(
                            "rounded-sm border p-2 text-sm transition-all duration-200",
                            open
                                ? "border-gray-300 shadow-lg"
                                : "border-gray-100 shadow-sm"
                        )}
                    >
                        <div className="flex items-center justify-between">
                            <strong>On</strong>
                            <div className="mx-10 flex-grow">
                                <SelectBox
                                    name="transition"
                                    options={transitionTypes}
                                    observeChange={(val) =>
                                        setTransitionType(val)
                                    }
                                />
                            </div>
                            <div className="space-x-2">
                                <button
                                    type="button"
                                    onClick={() => setOpen(!open)}
                                    className="rounded-full border border-gray-300 p-1 hover:border-gray-500 hover:shadow-sm"
                                >
                                    {open ? (
                                        <ChevronUpIcon className="h-4 w-4" />
                                    ) : (
                                        <ChevronDownIcon className="h-4 w-4" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        props.onRemove(props.task.uuid)
                                    }
                                    className="rounded-full border border-gray-300 p-1 hover:border-gray-500 hover:shadow-sm"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        <div className="mb-4 flex items-center space-x-2">
                            <div className="flex-grow">
                                <SelectBox
                                    name="contentType"
                                    id="contentType"
                                    options={contentTypes}
                                />
                            </div>
                            {transitionType === "meta-update" && (
                                <Fragment>
                                    <span>meta field</span>
                                    <div className="flex-grow">
                                        <SelectBox
                                            name="metaField"
                                            options={contentFlow.metaProperties[
                                                "content-lower"
                                            ]?.map((p) => {
                                                return {
                                                    value: p.field,
                                                    label: p.label,
                                                };
                                            })}
                                        />
                                    </div>
                                    <span>is</span>
                                    <div className="flex-grow">
                                        <TextInput name="metaTarget" />
                                    </div>
                                </Fragment>
                            )}
                            {transitionType === "transition" && (
                                <Fragment>
                                    <span>transitions from</span>
                                    <div className="flex-grow">
                                        <SelectBox
                                            name="fromWorkflow"
                                            options={contentFlow.workflows.map(
                                                (w) => {
                                                    return w.name;
                                                }
                                            )}
                                        />
                                    </div>
                                    <span>to</span>
                                    <div className="flex-grow">
                                        <SelectBox
                                            name="toWorkflow"
                                            options={contentFlow.workflows.map(
                                                (w) => {
                                                    return w.name;
                                                }
                                            )}
                                        />
                                    </div>
                                </Fragment>
                            )}
                        </div>
                        <div className={open ? "block" : "hidden"}>
                            <strong className="mb-2 block border-b border-gray-200 pb-4">
                                Then
                            </strong>
                            {actions.map((action, index) => (
                                <div
                                    key={index}
                                    className="mb-2 mb-4 space-y-2 border-b border-gray-200 pb-4"
                                >
                                    <div className="flex items-center space-x-2">
                                        <span>Action</span>
                                        <div className="flex-grow">
                                            <SelectBox
                                                name={`actions[${index}].type`}
                                                options={actionTypes}
                                                observeChange={(value) => {
                                                    const newActions = [
                                                        ...actions,
                                                    ];
                                                    newActions[index].type =
                                                        value;
                                                    setActions(newActions);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {action.type === "Send Email" && (
                                        <SendEmailAction index={index} />
                                    )}
                                    {action.type === "Publish" && (
                                        <PublishAction index={index} />
                                    )}
                                    {action.type === "Call Webhook" && (
                                        <CallWebhookAction index={index} />
                                    )}
                                    {action.type === "Set Tags" && (
                                        <SetTagsAction index={index} />
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addAction}
                                className="flex w-full items-center justify-center border border-dashed  border-gray-300 py-2 text-center text-gray-700 hover:border-gray-400"
                            >
                                <PlusIcon className="h-4 w-4" /> Add Action
                            </button>
                            <div className="mt-4 text-right">
                                <PrimaryButton type="submit">
                                    Save Task
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </Form>
            </Formik>
        </div>
    );
}
